import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import { listBranches } from '../../data/fetchers';
import { upsertUser } from '../../data/mutators';
import type { User } from '../../types/domain';

const blank = (): User => ({
  id: '',
  name: '',
  phone: '',
  email: '',
  role: 'cashier',
  branchIds: [],
  active: true,
  pin: '',
});

const randomPin = () => String(Math.floor(1000 + Math.random() * 9000));

export function UserFormModal({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: User | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [form, setForm] = useState<User>(blank());
  const [dashboardPassword, setDashboardPassword] = useState('');
  const [showDashboardPassword, setShowDashboardPassword] = useState(false);
  const [branchError, setBranchError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(user ? { ...user } : blank());
      setDashboardPassword('');
      setShowDashboardPassword(false);
      setBranchError('');
    }
  }, [open, user]);

  const mutation = useMutation({
    mutationFn: upsertUser,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const set = <K extends keyof User>(k: K, v: User[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id: string) =>
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(id)
        ? f.branchIds.filter((x) => x !== id)
        : [...f.branchIds, id],
    }));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !(form.phone ?? '').trim()) return;
    if (!user && dashboardPassword.trim().length < 8) return;
    if (form.branchIds.length === 0) {
      setBranchError('กรุณาเลือกอย่างน้อย 1 สาขา');
      return;
    }
    mutation.mutate({
      ...form,
      dashboardPassword: dashboardPassword.trim() || undefined,
    }); // id '' for new → API mutator assigns a stable UUID-backed id
  };

  return (
    <Modal
      open={open}
      title={user ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้'}
      description="กำหนดข้อมูลเข้าสู่ระบบ บทบาท และสาขาที่ผู้ใช้นี้เข้าถึงได้"
      onClose={onClose}
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Btn
            kind="default"
            type="button"
            className="w-full sm:w-auto"
            disabled={mutation.isPending}
            onClick={onClose}
          >
            ยกเลิก
          </Btn>
          <Btn
            kind="primary"
            type="submit"
            form="user-settings-form"
            className="w-full sm:w-auto"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'กำลังบันทึก' : 'บันทึกผู้ใช้'}
          </Btn>
        </div>
      }
    >
      <form
        id="user-settings-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
      >
        <Field label="ชื่อ" required>
          <Input
            aria-label="ชื่อ"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </Field>
        <Field label="เบอร์โทร" required hint="ใช้สำหรับระบุตัวผู้ใช้ในระบบ">
          <Input
            aria-label="เบอร์โทร"
            type="tel"
            inputMode="tel"
            value={form.phone ?? ''}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="0812345678"
          />
        </Field>
        <Field
          label="รหัสผ่านเข้า Dashboard"
          required={!user}
          hint={
            user
              ? 'เว้นว่างไว้หากไม่ต้องการเปลี่ยนรหัสผ่าน'
              : 'อย่างน้อย 8 ตัวอักษร'
          }
        >
          <div className="relative">
            <Input
              aria-label="รหัสผ่านเข้า Dashboard"
              type={showDashboardPassword ? 'text' : 'password'}
              value={dashboardPassword}
              onChange={(e) => setDashboardPassword(e.target.value)}
              className="pr-20"
              placeholder={
                user ? 'กรอกเมื่อต้องการตั้งใหม่' : 'อย่างน้อย 8 ตัวอักษร'
              }
            />
            <button
              type="button"
              onClick={() => setShowDashboardPassword((value) => !value)}
              className="absolute inset-y-0 right-2 px-2 text-xs-label font-bold text-ink-3 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label={
                showDashboardPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'
              }
            >
              {showDashboardPassword ? 'ซ่อน' : 'แสดง'}
            </button>
          </div>
        </Field>
        <Field label="บทบาท" required>
          <Select
            aria-label="บทบาท"
            value={form.role}
            onChange={(e) => set('role', e.target.value as User['role'])}
          >
            <option value="owner">เจ้าของ</option>
            <option value="manager">ผู้จัดการ</option>
            <option value="cashier">แคชเชียร์</option>
          </Select>
        </Field>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm-label font-semibold text-ink-2">สาขา</span>
          <div className="flex flex-wrap gap-3">
            {branches.map((b) => (
              <label key={b.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.branchIds.includes(b.id)}
                  onChange={() => {
                    toggleBranch(b.id);
                    setBranchError('');
                  }}
                />
                <span className="text-body">{b.short}</span>
              </label>
            ))}
          </div>
          {branchError && (
            <p role="alert" className="text-sm-label text-status-error">
              {branchError}
            </p>
          )}
        </div>
        {form.role !== 'owner' && (
          <Field
            label="PIN เข้า POS"
            hint="ตัวเลข 4 หลักสำหรับเข้าใช้งานเครื่อง POS"
          >
            <div className="flex gap-2 items-center">
              <Input
                aria-label="PIN เข้า POS"
                inputMode="numeric"
                maxLength={4}
                value={form.pin ?? ''}
                onChange={(e) =>
                  set('pin', e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                className="num w-28"
              />
              <Btn
                kind="default"
                size="sm"
                type="button"
                onClick={() => set('pin', randomPin())}
              >
                สุ่ม
              </Btn>
            </div>
          </Field>
        )}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => set('active', e.target.checked)}
          />
          <span className="text-body">เปิดใช้งาน</span>
        </label>
        {mutation.isError ? (
          <p role="alert" className="text-sm-label text-danger">
            บันทึกผู้ใช้ไม่สำเร็จ กรุณาตรวจสอบข้อมูลแล้วลองใหม่
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
